import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import api from '../api';

const formatWon = (amount) => `₩${Number(amount || 0).toLocaleString('ko-KR')}`;

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}요일</div>
        {payload.map((entry) => (
          <div key={entry.dataKey} style={{ fontSize: '0.82rem', color: entry.color }}>
            {entry.dataKey === 'revenue' ? `매출: ${formatWon(entry.value)}` : `주문: ${entry.value}건`}
          </div>
        ))}
      </div>
    );
  }
  return null;
};

let toastCounter = 0;
const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = 'info') => {
    const id = ++toastCounter;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  }, []);
  return { toasts, addToast };
};

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconXMark = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconInfo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const Toast = ({ toasts }) => (
  <div className="toast-container">
    {toasts.map((t) => (
      <div key={t.id} className={`toast ${t.type}`}>
        <span className="toast-icon">{t.type === 'success' ? <IconCheck /> : t.type === 'error' ? <IconXMark /> : <IconInfo />}</span>
        <div className="toast-content"><div className="toast-message">{t.msg}</div></div>
      </div>
    ))}
  </div>
);

const SettingsPage = () => {
  const { user, updateUser } = useAuth();
  const { toasts, addToast } = useToast();
  const storeId = user?.storeId || user?.store_id;
  const storeName = user?.name || user?.storeName || user?.store_name || '';

  const [operationMode, setOperationMode] = useState('post');
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [notifPermission, setNotifPermission] = useState('default');
  const [weeklyData, setWeeklyData] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [tables, setTables] = useState([]);
  const [storeInfo, setStoreInfo] = useState({ storeName: storeName, contactPhone: '' });
  const [soundOn, setSoundOn] = useState(true);
  const DAYS = ['월', '화', '수', '목', '금', '토', '일'];
  const defaultHours = () => DAYS.reduce((acc, d) => ({ ...acc, [d]: { open: '10:00', close: '22:00' } }), {});
  const [operatingHours, setOperatingHours] = useState(defaultHours());

  useEffect(() => {
    // Check notification permission
    if ('Notification' in window) {
      setNotifPermission(Notification.permission);
      setNotifEnabled(Notification.permission === 'granted');
    }

    // Fetch store settings and stats
    const fetchSettings = async () => {
      if (!storeId) {
        setWeeklyData([]);
        return;
      }

      setIsLoadingStats(true);
      try {
        const [settingsRes, statsRes, tablesRes] = await Promise.allSettled([
          api.get(`/admin/stores/${storeId}`),
          api.get(`/stores/${storeId}/stats/weekly`),
          api.get(`/stores/${storeId}/tables`),
        ]);

        if (settingsRes.status === 'fulfilled') {
          const store = settingsRes.value.data?.store || settingsRes.value.data;
          if (store?.operationMode) setOperationMode(store.operationMode);
          if (store?.storeName || store?.name) {
            setStoreInfo((p) => ({ ...p, storeName: store.storeName || store.name || storeName }));
          }
          if (store?.contactPhone) {
            setStoreInfo((p) => ({ ...p, contactPhone: store.contactPhone }));
          }
          if (store?.settings?.operating_hours) {
            setOperatingHours((prev) => ({ ...prev, ...store.settings.operating_hours }));
          }
          if (store?.settings?.notification_sound !== undefined) {
            setSoundOn(store.settings.notification_sound);
          }
        }

        if (statsRes.status === 'fulfilled') {
          const d = statsRes.value.data;
          setWeeklyData(Array.isArray(d) ? d : d.weekly || []);
        } else {
          setWeeklyData([]);
        }

        if (tablesRes.status === 'fulfilled') {
          const d = tablesRes.value.data;
          setTables(Array.isArray(d) ? d : d.tables || []);
        }
      } catch {
        setWeeklyData([]);
      } finally {
        setIsLoadingStats(false);
      }
    };

    fetchSettings();
  }, [storeId, storeName]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (storeId) {
        const res = await api.patch(`/admin/stores/${storeId}`, {
          operationMode,
          storeName: storeInfo.storeName,
          contactPhone: storeInfo.contactPhone,
          settings: {
            operating_hours: operatingHours,
            notification_sound: soundOn,
          },
        });
        const updatedStore = res.data?.store || res.data;
        if (updatedStore && updateUser) {
          updateUser({ ...user, storeName: storeInfo.storeName });
        }
      }
      addToast('설정이 저장되었습니다.', 'success');
    } catch {
      addToast('저장에 실패했습니다. 다시 시도해주세요.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleNotifToggle = async () => {
    if (!('Notification' in window)) {
      addToast('이 브라우저는 알림을 지원하지 않습니다.', 'error');
      return;
    }

    if (Notification.permission === 'granted') {
      setNotifEnabled(!notifEnabled);
      addToast(notifEnabled ? '알림이 비활성화되었습니다.' : '알림이 활성화되었습니다.', 'success');
    } else if (Notification.permission === 'denied') {
      addToast('브라우저 설정에서 알림 권한을 허용해주세요.', 'warning');
    } else {
      const perm = await Notification.requestPermission();
      setNotifPermission(perm);
      if (perm === 'granted') {
        setNotifEnabled(true);
        addToast('알림 권한이 허용되었습니다.', 'success');
      } else {
        addToast('알림 권한이 거부되었습니다.', 'error');
      }
    }
  };

  const handleTestNotif = () => {
    if (notifEnabled && Notification.permission === 'granted') {
      new Notification('스마트오더 알림 테스트', {
        body: '알림이 정상적으로 작동합니다!',
        icon: '/favicon.ico',
      });
      addToast('테스트 알림이 전송되었습니다.', 'success');
    } else {
      addToast('알림을 먼저 활성화해주세요.', 'warning');
    }
  };

  const handleDownloadQRAll = async () => {
    if (tables.length === 0) {
      addToast('다운로드할 테이블이 없습니다.', 'warning');
      return;
    }
    // Download each table QR one by one
    tables.forEach((table, idx) => {
      const tableId = table._id || table.id;
      const tableNum = table.tableNumber || table.table_number;
      const qrUrl = `http://localhost:3000/order/${storeId}/${tableId}`;
      const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrUrl)}`;
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = qrApiUrl;
        link.download = `table-${tableNum}-qr.png`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, idx * 500);
    });
    addToast(`${tables.length}개 QR 코드 다운로드를 시작합니다.`, 'success');
  };

  const totalWeeklyRevenue = weeklyData.reduce((s, d) => s + (d.revenue || 0), 0);
  const totalWeeklyOrders = weeklyData.reduce((s, d) => s + (d.orders || 0), 0);

  return (
    <div className="page-container">
      <Toast toasts={toasts} />

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">설정</h1>
          <p className="page-subtitle">매장 설정 및 운영 환경을 관리합니다</p>
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? <><span className="spinner spinner-sm" /> 저장 중...</> : '저장하기'}
        </button>
      </div>

      {/* Store info */}
      <div className="settings-section">
        <div className="settings-section-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          매장 정보
        </div>
        <div className="settings-row">
          <div className="settings-row-info">
            <h4>매장 이름</h4>
            <p>고객에게 보여지는 매장 이름입니다</p>
          </div>
          <input
            type="text"
            className="form-input"
            style={{ maxWidth: 280 }}
            value={storeInfo.storeName}
            onChange={(e) => setStoreInfo((p) => ({ ...p, storeName: e.target.value }))}
            placeholder="매장 이름"
          />
        </div>
        <div className="settings-row">
          <div className="settings-row-info">
            <h4>연락처</h4>
            <p>매장 전화번호</p>
          </div>
          <input
            type="tel"
            className="form-input"
            style={{ maxWidth: 280 }}
            value={storeInfo.contactPhone}
            onChange={(e) => setStoreInfo((p) => ({ ...p, contactPhone: e.target.value }))}
            placeholder="010-0000-0000"
          />
        </div>
        <div className="settings-row">
          <div className="settings-row-info">
            <h4>이메일</h4>
            <p>로그인 계정 이메일</p>
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', background: 'var(--bg)', padding: '9px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
            {user?.email || '-'}
          </div>
        </div>
      </div>

      {/* Operation mode */}
      <div className="settings-section">
        <div className="settings-section-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
          운영 모드
        </div>
        <div className="settings-row">
          <div className="settings-row-info">
            <h4>결제 방식</h4>
            <p>
              {operationMode === 'pre'
                ? '선결제: 고객이 주문 전에 미리 결제합니다'
                : '후결제: 고객이 식사 후 결제합니다'}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: operationMode === 'post' ? 'var(--text-tertiary)' : 'var(--primary)',
              transition: 'color 0.2s',
            }}>
              선결제
            </span>
            <label className="toggle-switch" style={{ cursor: 'pointer' }}>
              <div
                className={`toggle-track ${operationMode === 'pre' ? 'on' : ''}`}
                onClick={() => setOperationMode(operationMode === 'pre' ? 'post' : 'pre')}
                style={{ cursor: 'pointer' }}
              >
                <div className="toggle-thumb" />
              </div>
            </label>
            <span style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: operationMode === 'pre' ? 'var(--text-tertiary)' : 'var(--success)',
              transition: 'color 0.2s',
            }}>
              후결제
            </span>
          </div>
        </div>

        <div style={{ padding: '14px 24px', background: 'rgba(191,155,48,0.04)', borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{
              padding: '14px 16px',
              borderRadius: 'var(--radius-sm)',
              border: `2px solid ${operationMode === 'pre' ? 'var(--primary)' : 'var(--border)'}`,
              background: operationMode === 'pre' ? 'rgba(191,155,48,0.06)' : '#fff',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }} onClick={() => setOperationMode('pre')}>
              <div style={{ fontWeight: 700, marginBottom: 6, color: operationMode === 'pre' ? 'var(--primary)' : 'var(--text)' }}>
                선결제 (Pre-payment)
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                • 주문 전 결제 완료<br />
                • 미수 걱정 없음<br />
                • 카페, 패스트푸드에 적합
              </div>
            </div>
            <div style={{
              padding: '14px 16px',
              borderRadius: 'var(--radius-sm)',
              border: `2px solid ${operationMode === 'post' ? 'var(--success)' : 'var(--border)'}`,
              background: operationMode === 'post' ? 'rgba(0,191,64,0.06)' : '#fff',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }} onClick={() => setOperationMode('post')}>
              <div style={{ fontWeight: 700, marginBottom: 6, color: operationMode === 'post' ? 'var(--success)' : 'var(--text)' }}>
                후결제 (Post-payment)
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                • 식사 후 결제<br />
                • 추가 주문 편리<br />
                • 레스토랑, 한식당에 적합
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Operating hours */}
      <div className="settings-section">
        <div className="settings-section-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          운영시간
        </div>
        <div style={{ padding: '16px 24px' }}>
          <div style={{ display: 'grid', gap: 10 }}>
            {DAYS.map((day) => (
              <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 30, fontWeight: 600, fontSize: '0.9rem', textAlign: 'center' }}>{day}</span>
                <input
                  type="time"
                  className="form-input"
                  style={{ maxWidth: 130, padding: '6px 10px', fontSize: '0.85rem' }}
                  value={operatingHours[day]?.open || '10:00'}
                  onChange={(e) => setOperatingHours((prev) => ({ ...prev, [day]: { ...prev[day], open: e.target.value } }))}
                />
                <span style={{ color: 'var(--text-tertiary)' }}>~</span>
                <input
                  type="time"
                  className="form-input"
                  style={{ maxWidth: 130, padding: '6px 10px', fontSize: '0.85rem' }}
                  value={operatingHours[day]?.close || '22:00'}
                  onChange={(e) => setOperatingHours((prev) => ({ ...prev, [day]: { ...prev[day], close: e.target.value } }))}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Notification settings */}
      <div className="settings-section">
        <div className="settings-section-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          알림 설정
        </div>
        <div className="settings-row">
          <div className="settings-row-info">
            <h4>브라우저 알림</h4>
            <p>
              {notifPermission === 'denied'
                ? '브라우저에서 알림이 차단되어 있습니다'
                : notifEnabled
                  ? '새 주문이 들어오면 알림을 받습니다'
                  : '알림을 활성화하면 새 주문을 즉시 알 수 있습니다'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button
              className="btn btn-outline btn-sm"
              onClick={handleTestNotif}
              disabled={!notifEnabled}
            >
              테스트
            </button>
            <label className="toggle-switch" style={{ cursor: 'pointer' }}>
              <div
                className={`toggle-track ${notifEnabled ? 'on' : ''}`}
                onClick={handleNotifToggle}
                style={{ cursor: 'pointer' }}
              >
                <div className="toggle-thumb" />
              </div>
            </label>
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-row-info">
            <h4>알림음 설정</h4>
            <p>{soundOn ? '새 주문/호출 시 알림음이 재생됩니다' : '알림음이 꺼져 있습니다'}</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <label className="toggle-switch" style={{ cursor: 'pointer' }}>
              <div
                className={`toggle-track ${soundOn ? 'on' : ''}`}
                onClick={() => setSoundOn(!soundOn)}
                style={{ cursor: 'pointer' }}
              >
                <div className="toggle-thumb" />
              </div>
            </label>
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-row-info">
            <h4>브라우저 알림 권한</h4>
            <p>
              {notifPermission === 'granted' ? '알림이 허용되었습니다' : notifPermission === 'denied' ? '알림이 차단되었습니다. 브라우저 설정에서 변경해주세요.' : '알림 권한을 요청해주세요'}
            </p>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={async () => {
              if ('Notification' in window && Notification.permission === 'default') {
                const perm = await Notification.requestPermission();
                setNotifPermission(perm);
                if (perm === 'granted') { setNotifEnabled(true); addToast('알림 권한이 허용되었습니다.', 'success'); }
                else addToast('알림 권한이 거부되었습니다.', 'error');
              } else if (Notification.permission === 'granted') {
                addToast('이미 알림이 허용되었습니다.', 'info');
              } else {
                addToast('브라우저 설정에서 알림을 허용해주세요.', 'warning');
              }
            }}
          >
            권한 요청
          </button>
        </div>

        <div className="settings-row">
          <div className="settings-row-info">
            <h4>주문 알림음</h4>
            <p>새 주문 시 알림음이 재생됩니다 (Web Audio)</p>
          </div>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => {
              try {
                const ctx = new (window.AudioContext || window.webkitAudioContext)();
                [880, 1100, 880].forEach((freq, i) => {
                  const osc = ctx.createOscillator();
                  const gain = ctx.createGain();
                  osc.connect(gain);
                  gain.connect(ctx.destination);
                  osc.frequency.value = freq;
                  const t = ctx.currentTime + i * 0.2;
                  gain.gain.setValueAtTime(0.25, t);
                  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
                  osc.start(t);
                  osc.stop(t + 0.3);
                });
                addToast('알림음 테스트!', 'success');
              } catch {
                addToast('오디오를 재생할 수 없습니다.', 'error');
              }
            }}
          >
            테스트
          </button>
        </div>
      </div>

      {/* QR bulk download */}
      <div className="settings-section">
        <div className="settings-section-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="5" y="2" width="14" height="20" rx="2"/>
            <line x1="12" y1="18" x2="12" y2="18" strokeWidth="3" strokeLinecap="round"/>
          </svg>
          QR 코드 관리
        </div>
        <div className="settings-row">
          <div className="settings-row-info">
            <h4>QR 코드 일괄 다운로드</h4>
            <p>
              {tables.length > 0
                ? `전체 ${tables.length}개 테이블의 QR 코드를 다운로드합니다`
                : '테이블 관리에서 테이블을 먼저 추가해주세요'}
            </p>
          </div>
          <button
            className="btn btn-outline"
            onClick={handleDownloadQRAll}
            disabled={tables.length === 0}
          >
            전체 QR 다운로드 ({tables.length}개)
          </button>
        </div>

        {/* Table list with QR links */}
        {tables.length > 0 && (
          <div style={{ padding: '12px 24px 16px', borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
              {tables.map((table) => {
                const tableNum = table.tableNumber || table.table_number;
                const tableId = table._id || table.id;
                const qrUrl = `http://localhost:3000/order/${storeId}/${tableId}`;
                const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}`;
                return (
                  <div
                    key={tableId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 12px',
                      background: 'var(--bg)',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)',
                    }}
                  >
                    <div style={{ width: 32, height: 32, background: 'var(--primary-light)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem', color: 'var(--primary)' }}>
                      {tableNum}
                    </div>
                    <span style={{ fontSize: '0.85rem', flex: 1, fontWeight: 500 }}>테이블 {tableNum}번</span>
                    <a
                      href={qrApiUrl}
                      download={`table-${tableNum}-qr.png`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'var(--primary)', display: 'flex', alignItems: 'center' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Weekly stats */}
      <div className="settings-section">
        <div className="settings-section-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
          </svg>
          주간 매출 통계
          {isLoadingStats && <span className="spinner spinner-dark spinner-sm" style={{ marginLeft: 10 }} />}
        </div>

        {/* Summary numbers */}
        <div style={{ padding: '16px 24px', display: 'flex', gap: 24, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 3 }}>이번 주 매출</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--primary)' }}>
              {formatWon(totalWeeklyRevenue)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 3 }}>이번 주 주문</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--success)' }}>
              {totalWeeklyOrders}건
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 3 }}>평균 객단가</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text)' }}>
              {totalWeeklyOrders > 0 ? formatWon(Math.round(totalWeeklyRevenue / totalWeeklyOrders)) : '-'}
            </div>
          </div>
        </div>

        <div style={{ padding: '20px 24px' }}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={weeklyData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="revenue"
                orientation="left"
                tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `₩${(v / 1000).toFixed(0)}k`}
              />
              <YAxis
                yAxisId="orders"
                orientation="right"
                tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '12px', paddingTop: 16 }}
                formatter={(value) => value === 'revenue' ? '매출 (원)' : '주문 (건)'}
              />
              <Bar
                yAxisId="revenue"
                dataKey="revenue"
                fill="var(--primary)"
                radius={[4, 4, 0, 0]}
                opacity={0.85}
              />
              <Bar
                yAxisId="orders"
                dataKey="orders"
                fill="var(--success)"
                radius={[4, 4, 0, 0]}
                opacity={0.7}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Save button (bottom) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={isSaving}>
          {isSaving ? <><span className="spinner spinner-sm" /> 저장 중...</> : '저장하기'}
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;
